import 'intl-tel-input/styles';

import IntlTelInput from 'intl-tel-input/reactWithUtils';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import COLORS from '../constants/colors';

export default function AuthPhoneInput({
  id,
  label,
  value,
  onChange,
  onCountryChange,
  defaultCountry,
  inputRef: externalInputRef,
  error,
  last,
  disabled,
  ...inputProps
}) {
  const { t } = useTranslation();
  const intlTelInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onCountryChangeRef = useRef(onCountryChange);
  const valueRef = useRef(value || '');
  const isSyncingRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCountryChangeRef.current = onCountryChange;
  }, [onCountryChange]);

  useEffect(() => {
    valueRef.current = value || '';
  }, [value]);

  useEffect(() => {
    if (!externalInputRef) {
      return undefined;
    }
    const inputElement = intlTelInputRef.current
      ? intlTelInputRef.current.getInput()
      : null;
    externalInputRef(inputElement);

    return () => {
      externalInputRef(null);
    };
  }, [externalInputRef]);

  useEffect(() => {
    const instance = intlTelInputRef.current
      ? intlTelInputRef.current.getInstance()
      : null;
    if (!instance) {
      return;
    }

    const currentValue = instance.getNumber() || '';
    const nextValue = value || '';
    if (currentValue === nextValue) {
      return;
    }

    isSyncingRef.current = true;
    if (value) {
      instance.setNumber(value);
    } else {
      const inputEl = intlTelInputRef.current.getInput();
      if (inputEl) {
        inputEl.value = '';
      }
    }
    isSyncingRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!defaultCountry || defaultCountry === 'auto') {
      return;
    }
    const instance = intlTelInputRef.current
      ? intlTelInputRef.current.getInstance()
      : null;
    if (instance) {
      instance.setCountry(defaultCountry);
    }
  }, [defaultCountry]);

  const initOptions = useMemo(
    () => ({
      strictMode: true,
      initialCountry: defaultCountry || 'auto',
    }),
    [defaultCountry],
  );

  const handleNumberChange = useCallback((number) => {
    if (isSyncingRef.current) {
      return;
    }
    const nextValue = number || '';
    if (nextValue === valueRef.current) {
      return;
    }
    if (!onChangeRef.current) {
      return;
    }
    valueRef.current = nextValue;
    onChangeRef.current({
      target: {
        value: nextValue,
      },
    });
  }, []);

  const handleCountryChange = useCallback((countryIso) => {
    const handler = onCountryChangeRef.current;
    if (!handler) {
      return;
    }
    const normalizedCountry =
      typeof countryIso === 'string' ? countryIso.toUpperCase() : '';
    if (!normalizedCountry) {
      return;
    }
    handler(normalizedCountry);
  }, []);

  const {
    style: inlineInputStyle,
    className,
    test_id,
    ...otherInputProps
  } = inputProps;

  const mergedClassName = ['form-control', className].filter(Boolean).join(' ');

  const errorMessage =
    error && error !== '*'
      ? t(`validators.${error}`)
      : error === '*'
        ? '*'
        : '';

  return (
    <div id={`${id}_container`} style={{ marginBottom: 5 }}>
      <div
        style={{
          color: COLORS.MAIN_GRAY,
          alignSelf: 'center',
          fontSize: 14,
          float: 'left',
        }}>
        {label ? label.toUpperCase() : ''}
      </div>
      {error && (
        <div
          style={{
            float: 'right',
            marginRight: 10,
            color: COLORS.MAIN_RED,
            alignSelf: 'center',
            fontSize: 14,
            left: 3,
          }}>
          {errorMessage}
        </div>
      )}
      <div style={{ clear: 'both' }}></div>
      <IntlTelInput
        ref={intlTelInputRef}
        disabled={disabled}
        initialValue={value}
        onChangeNumber={handleNumberChange}
        onChangeCountry={handleCountryChange}
        initOptions={initOptions}
        inputProps={{
          id,
          'aria-label': test_id,
          className: mergedClassName,
          style: {
            border: 'none',
            borderRadius: 0,
            paddingLeft: 0,
            backgroundColor: 'white',
            color: COLORS.MAIN_BLUE,
            width: '100%',
            ...inlineInputStyle,
          },
          ...otherInputProps,
        }}
      />
      {!last && (
        <div
          style={{
            width: '100%',
            height: 1,
            marginTop: 5,
            backgroundColor: COLORS.GRAY_OUTLINE,
          }}></div>
      )}
    </div>
  );
}

AuthPhoneInput.defaultProps = {
  value: '',
  onCountryChange: undefined,
  defaultCountry: undefined,
  error: undefined,
  last: false,
  disabled: false,
};

AuthPhoneInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func,
  defaultCountry: PropTypes.string,
  inputRef: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  last: PropTypes.bool,
  disabled: PropTypes.bool,
};
